import GamesConfig from "../config/Games";

// New standard games opt in with roomInvite: {} in Games.js; no new hook or native parser is needed.
export function createRoomInviteRegistry(games) {
  return games.filter(game => game.roomInvite && game.roomInvite.enabled !== false).map(game => ({
    gameId: String(game.id), gameVersion: game.type,
    roomParam: "room_id", tierParam: "subid", hallParam: "hall_url",
    roomPattern: "^[A-Za-z0-9_:-]{1,128}$", tierMin: 0,
    ...game.roomInvite,
  }));
}

export const ROOM_INVITE_REGISTRY = createRoomInviteRegistry(GamesConfig.games);
const ATTRIBUTION = ["aid", "f", "c", "p"];
const AGENT = /^[1-9][0-9]{0,19}$/;
export const roomInviteConfig = (gameId, registry = ROOM_INVITE_REGISTRY) =>
  registry.find(config => config.gameId === String(gameId));

export function hasRoomInviteFields(search, registry = ROOM_INVITE_REGISTRY) {
  const query = new URLSearchParams(search);
  return ["invite_token", "invite", "room", "tier"].some(key => query.has(key)) || registry.some(config =>
    config.legacy && [config.roomParam, config.tierParam].some(key => query.has(key)));
}

export function readRoomInvite(search, registry = ROOM_INVITE_REGISTRY) {
  const query = new URLSearchParams(search);
  // A server invitation token must be handled by its own hall protocol, never as a plain room number.
  if (query.has("invite_token")) return null;
  const generic = ["invite", "room", "tier"].some(key => query.has(key));
  const aliases = registry.filter(config => config.legacy &&
    [config.roomParam, config.tierParam].some(key => query.has(key)));
  if ((generic && aliases.length) || aliases.length > 1) return null;
  const unique = key => query.getAll(key).length === 1;
  let config, roomId, tier;
  if (generic) {
    if (!["invite", "game_id", "room", "tier"].every(unique) || query.get("invite") !== "1") return null;
    config = roomInviteConfig(query.get("game_id"), registry);
    roomId = query.get("room"); tier = query.get("tier");
  } else {
    config = aliases[0];
    if (!config || !unique(config.roomParam) || query.getAll(config.tierParam).length > 1 || query.getAll("game_id").length > 1) return null;
    if (query.has("game_id") && query.get("game_id") !== config.gameId) return null;
    roomId = query.get(config.roomParam);
    tier = query.has(config.tierParam) ? query.get(config.tierParam) : config.defaultTier;
  }
  if (!config || !/^(0|[1-9][0-9]*)$/.test(tier || "") || !Number.isSafeInteger(Number(tier)) ||
    Number(tier) < config.tierMin || (config.tiers && !config.tiers.includes(tier))) return null;
  try { if (!new RegExp(config.roomPattern).test(roomId || "")) return null; } catch (_) { return null; }
  const invite = { gameId: config.gameId, roomId, tier };
  for (const key of ATTRIBUTION) {
    if (query.getAll(key).length > 1) return null;
    const value = query.get(key);
    if (value && value !== "undefined" && value !== "null" && value.trim() === value &&
        !/[\u0000-\u001f\u007f]/.test(value) && (key === "aid" ? AGENT.test(value) : value.length <= 128)) invite[key] = value;
  }
  return invite;
}

export function roomInviteHomePath(search, registry = ROOM_INVITE_REGISTRY, preserveLegacy = false) {
  const invite = readRoomInvite(search, registry);
  if (!invite) return null;
  const config = roomInviteConfig(invite.gameId, registry), incoming = new URLSearchParams(search);
  const legacy = preserveLegacy && config.legacy && !incoming.has("invite");
  const query = legacy ? new URLSearchParams({ [config.roomParam]: invite.roomId, [config.tierParam]: invite.tier }) :
    new URLSearchParams({ invite: "1", game_id: invite.gameId, room: invite.roomId, tier: invite.tier });
  ATTRIBUTION.forEach(key => { if (invite[key]) query.set(key, invite[key]); });
  return `/home?${query.toString()}`;
}

export function roomInviteGameUrl(search, getGameUrl, hallOrigin, registry = ROOM_INVITE_REGISTRY) {
  const invite = readRoomInvite(search, registry);
  if (!invite) return null;
  const config = roomInviteConfig(invite.gameId, registry);
  const url = new URL(getGameUrl(config.gameVersion), hallOrigin);
  url.searchParams.set("game_id", invite.gameId);
  url.searchParams.set(config.roomParam, invite.roomId);
  url.searchParams.set(config.tierParam, invite.tier);
  url.searchParams.set(config.hallParam, new URL("/home", hallOrigin).href);
  return url.href;
}

export function roomInviteGamePath(search, getGameUrl, hallOrigin, registry = ROOM_INVITE_REGISTRY) {
  // Old APKs acknowledge legacy room keys on /game and inside login back_to.
  // Keep that wire format for old links; native v1 deliveries are already canonical.
  const home = roomInviteHomePath(search, registry, true);
  if (!home) return null;
  const query = new URLSearchParams(home.split("?")[1]);
  query.set("game_id", readRoomInvite(query, registry).gameId);
  query.set("url", roomInviteGameUrl(query, getGameUrl, hallOrigin, registry));
  return `/game?${query.toString()}`;
}

export function gameUrlWithInviteContext(gameUrl, gameId, hallOrigin, registry = ROOM_INVITE_REGISTRY) {
  const config = roomInviteConfig(gameId, registry);
  if (!config) return gameUrl;
  const url = new URL(gameUrl, hallOrigin);
  url.searchParams.set("game_id", String(gameId));
  url.searchParams.set(config.hallParam, new URL("/home", hallOrigin).href);
  return url.href;
}

// The hall route owns the invited room; stale room fields inside a cached iframe URL cannot override it.
export function gameUrlWithRoomInvite(gameUrl, gameId, hallOrigin, outerSearch, registry = ROOM_INVITE_REGISTRY) {
  const config = roomInviteConfig(gameId, registry);
  if (!config) return gameUrl;
  const url = new URL(gameUrlWithInviteContext(gameUrl, gameId, hallOrigin, registry));
  if (hasRoomInviteFields(outerSearch, registry)) {
    url.searchParams.delete(config.roomParam);
    url.searchParams.delete(config.tierParam);
    const invite = readRoomInvite(outerSearch, registry);
    if (invite && invite.gameId === String(gameId)) {
      url.searchParams.set(config.roomParam, invite.roomId);
      url.searchParams.set(config.tierParam, invite.tier);
    }
  }
  return url.href;
}

export function roomInviteRegistrationPath(homePath) {
  const query = new URLSearchParams({ reg: "1", back_to: homePath });
  const channel = new URLSearchParams(homePath.split("?")[1]).get("c");
  if (channel) query.set("c", channel);
  return `/login?${query.toString()}`;
}

export const isRoomInviteRegisteredPlayer = user => Boolean(user && user.token && !user.is_guest && !user.is_demo);
